import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Database, Save, RefreshCw, Trash2, AlertTriangle, Search } from 'lucide-react';
import { erpToast } from './erp/ERPToast';
import { useTierList } from '../lib/useTierList';
import { API, apiFetch } from '../lib/api';

/* ── Type-ahead DB picker ──────────────────────────────────────────────────
   Replaces <datalist> — works for 10,000+ DBs by filtering client-side on
   keystroke and showing max 15 matches in a portal-style dropdown.         */
function DBPicker({ value, onChange, placeholder, availableDbs, testId }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(value || '');
  const ref = useRef(null);

  // Keep local query in sync when parent resets value
  useEffect(() => { setQuery(value || ''); }, [value]);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q || q.length < 1) return [];
    return availableDbs.filter(db => db.toLowerCase().includes(q)).slice(0, 15);
  }, [query, availableDbs]);

  // Close on outside click
  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const pick = (db) => { onChange(db); setQuery(db); setOpen(false); };

  return (
    <div ref={ref} style={{ position: 'relative', width: '100%' }}>
      <div style={{ position: 'relative' }}>
        <Search size={11} style={{ position: 'absolute', left: 7, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', pointerEvents: 'none' }} />
        <input
          data-testid={testId}
          className="form-input"
          value={query}
          onChange={e => { setQuery(e.target.value); onChange(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder || 'Type to search DBs…'}
          style={{ width: '100%', fontFamily: 'Source Code Pro, monospace', fontSize: 11, paddingLeft: 24 }}
        />
      </div>
      {open && matches.length > 0 && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 9999,
          background: '#fff', border: '1px solid #bfdbfe', borderRadius: 6,
          boxShadow: '0 8px 24px rgba(15,23,42,0.14)', maxHeight: 220, overflow: 'auto'
        }}>
          {matches.map(db => (
            <div key={db}
              onMouseDown={() => pick(db)}
              style={{
                padding: '6px 10px', fontSize: 11, fontFamily: 'Source Code Pro, monospace',
                cursor: 'pointer', color: '#1e40af',
                borderBottom: '1px solid #eff6ff',
              }}
              onMouseEnter={e => e.currentTarget.style.background = '#eff6ff'}
              onMouseLeave={e => e.currentTarget.style.background = ''}
            >{db}</div>
          ))}
          {availableDbs.length > 15 && query.length >= 1 && (
            <div style={{ padding: '4px 10px', fontSize: 10, color: '#94a3b8', fontStyle: 'italic' }}>
              Showing top 15 of {availableDbs.filter(d => d.toLowerCase().includes(query.toLowerCase())).length} matches
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * TemplateDBPanel — manages provisioning-template-DB resolution for tenant
 * sign-ups. Layered model (resolution order at sign-up time):
 *   1. Industry × Tier override   (WebApp.UAMS_IndustryTemplateDB)
 *   2. Industry default           (tbl_industry_master.TemplateDB)
 *   3. System fallback             ('RC26ERP_DEV')
 *
 * UI is collapsible to keep the parent Industry Mapping screen lean. The
 * team that manages tenant DB cloning lives here.
 */

export default function TemplateDBPanel() {
  const [collapsed, setCollapsed]     = useState(false);
  const [loading, setLoading]         = useState(true);
  const [saving, setSaving]           = useState(false);
  const [industries, setIndustries]   = useState([]);
  const [overrides, setOverrides]     = useState([]);
  const [defaults, setDefaults]       = useState({});            // industryCode -> templateDB
  const [overrideMap, setOverrideMap] = useState({});            // `${ic}|${tc}` -> templateDB
  const [pristine, setPristine]       = useState({ defaults: {}, overrideMap: {} });
  const [filter, setFilter]           = useState('');
  const [availableDbs, setAvailableDbs] = useState([]);

  const { tiers, loading: tiersLoading } = useTierList();

  const loadAvailableDbs = useCallback(async () => {
    try {
      const r = await apiFetch(`${API}/uams/available-dbs`);
      if (r.ok) { const d = await r.json(); setAvailableDbs((d.dbs || []).map(db => db.DbName)); }
    } catch (_) {}
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiFetch(`${API}/uams/industry-templates`);
      const d = await r.json();
      if (!r.ok || d.error) throw new Error(d.error || `HTTP ${r.status}`);
      setIndustries(d.industries || []);
      setOverrides(d.overrides || []);
      const nextDefaults = {};
      (d.industries || []).forEach(i => { nextDefaults[i.IndustryCode] = i.TemplateDB || ''; });
      const nextOverrides = {};
      (d.overrides || []).forEach(o => { nextOverrides[`${o.IndustryCode}|${o.TierCode}`] = o.TemplateDBName; });
      setDefaults(nextDefaults);
      setOverrideMap(nextOverrides);
      setPristine({ defaults: { ...nextDefaults }, overrideMap: { ...nextOverrides } });
    } catch (e) {
      erpToast.error(`Template DB load failed: ${e.message}`);
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); loadAvailableDbs(); }, [load, loadAvailableDbs]);

  const dirty = useMemo(() => {
    return JSON.stringify(defaults)    !== JSON.stringify(pristine.defaults)
        || JSON.stringify(overrideMap) !== JSON.stringify(pristine.overrideMap);
  }, [defaults, overrideMap, pristine]);

  const filteredIndustries = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return industries;
    return industries.filter(i =>
      `${i.IndustryCode} ${i.IndustryName} ${i.Category || ''}`.toLowerCase().includes(q)
    );
  }, [industries, filter]);

  const setDefault = (code, value) => setDefaults(d => ({ ...d, [code]: value }));
  const setOverride = (code, tier, value) => setOverrideMap(o => ({ ...o, [`${code}|${tier}`]: value }));
  const deleteOverride = (code, tier) => setOverrideMap(o => {
    const next = { ...o }; delete next[`${code}|${tier}`]; return next;
  });

  const save = async () => {
    setSaving(true);
    try {
      const tasks = [];
      // 1) Industry defaults — only ones that changed.
      for (const code of Object.keys(defaults)) {
        if ((defaults[code] || '') !== (pristine.defaults[code] || '')) {
          tasks.push(apiFetch(`${API}/uams/industries/${encodeURIComponent(code)}/template-db`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ templateDB: defaults[code] || null })
          }));
        }
      }
      // 2) Industry × Tier overrides — adds/updates.
      for (const key of Object.keys(overrideMap)) {
        if ((overrideMap[key] || '') !== (pristine.overrideMap[key] || '')) {
          const [industryCode, tierCode] = key.split('|');
          tasks.push(apiFetch(`${API}/uams/industry-tier-template`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ industryCode, tierCode, templateDB: overrideMap[key] || null })
          }));
        }
      }
      // 3) Deletes — keys that existed before but no longer.
      for (const key of Object.keys(pristine.overrideMap)) {
        if (!(key in overrideMap)) {
          const [industryCode, tierCode] = key.split('|');
          tasks.push(apiFetch(`${API}/uams/industry-tier-template`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ industryCode, tierCode, templateDB: null })
          }));
        }
      }
      const results = await Promise.all(tasks);
      const failures = [];
      for (const r of results) {
        if (!r.ok) {
          try { const d = await r.json(); failures.push(d.error || `HTTP ${r.status}`); }
          catch { failures.push(`HTTP ${r.status}`); }
        }
      }
      if (failures.length) {
        erpToast.error(`Saved with ${failures.length} failure(s): ${failures[0]}`);
      } else {
        erpToast.success(`Template DB saved · ${tasks.length} change(s)`);
      }
      await load();
    } catch (e) {
      erpToast.error(`Save failed: ${e.message}`);
    }
    setSaving(false);
  };

  return (
    <div data-testid="template-db-panel" style={{
      border: '1px solid var(--erp-border, #bfdbfe)',
      borderRadius: 8, background: 'var(--bg-surface, #fff)', overflow: 'hidden'
    }}>
      {/* Collapsible header */}
      <button
        onClick={() => setCollapsed(c => !c)}
        data-testid="template-db-toggle"
        style={{
          width: '100%', textAlign: 'left',
          padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10,
          background: 'var(--erp-grid-row-hover, #eff6ff)',
          border: 'none', borderBottom: collapsed ? 'none' : '1px solid var(--erp-border, #bfdbfe)',
          cursor: 'pointer', fontSize: 12, color: '#1e40af', fontWeight: 700
        }}
      >
        <Database size={14} />
        <span>Template DB — per-industry default + Industry × Tier overrides</span>
        <span style={{ marginLeft: 'auto', fontSize: 10, color: '#3b82f6', fontWeight: 600 }}>
          {industries.length} industries · {tiers.length} tiers · {Object.keys(overrideMap).length} overrides
          {availableDbs.length > 0 && <span style={{ color: '#6366f1', marginLeft: 6 }}>· {availableDbs.length} DBs available</span>}
          {dirty && <span style={{ color: '#b45309', marginLeft: 8 }}>● unsaved</span>}
        </span>
        <span style={{ fontSize: 12, color: '#3b82f6' }}>{collapsed ? '▸' : '▾'}</span>
      </button>

      {!collapsed && (
      <div style={{ padding: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, color: '#475569', marginBottom: 10 }}>
          <span><strong>Resolution order at signup:</strong> Industry × Tier override → Industry default → <code>RC26ERP_DEV</code></span>
        </div>

        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10 }}>
          <input
            className="form-input"
            placeholder="Filter industries…"
            value={filter}
            onChange={e => setFilter(e.target.value)}
            data-testid="template-db-filter"
            style={{ flex: '0 0 240px' }}
          />
          <button onClick={() => { load(); loadAvailableDbs(); }} className="erp-btn" disabled={loading || saving} data-testid="template-db-reload" title="Reload">
            <RefreshCw size={12} className={loading ? 'spin' : ''} />
          </button>
          <button onClick={save} className="erp-btn erp-btn-primary" disabled={!dirty || saving} data-testid="template-db-save" style={{ marginLeft: 'auto' }}>
            <Save size={12} /> {saving ? 'Saving…' : dirty ? 'Save changes' : 'Saved'}
          </button>
        </div>

        {/* Remove old datalist — replaced by DBPicker type-ahead */}

        {loading ? (
          <div style={{ padding: 30, textAlign: 'center', color: '#94a3b8' }}>Loading…</div>
        ) : filteredIndustries.length === 0 ? (
          <div style={{ padding: 30, textAlign: 'center', color: '#94a3b8' }}>No industries match the filter.</div>
        ) : (
        <div style={{ maxHeight: 360, overflow: 'auto', border: '1px solid #e2e8f0', borderRadius: 6 }}>
          <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: 11 }}>
            <thead>
              <tr style={{ background: '#f1f5f9', position: 'sticky', top: 0 }}>
                <th style={th(180)}>Industry</th>
                <th style={th(200)}>Default Template DB</th>
                {tiers.map(t => <th key={t} style={th(150)} title={`Override Template DB for ${t}`}>{t} override</th>)}
              </tr>
            </thead>
            <tbody>
              {filteredIndustries.map(i => (
                <tr key={i.IndustryCode}>
                  <td style={td(180)} title={i.IndustryCode}>
                    <div style={{ fontWeight: 700, color: '#0f172a' }}>{i.IndustryName}</div>
                    <div style={{ fontSize: 9, color: '#94a3b8', letterSpacing: '0.05em' }}>{i.IndustryCode}</div>
                  </td>
                  <td style={td(200)}>
                    <DBPicker
                      value={defaults[i.IndustryCode] || ''}
                      onChange={v => setDefault(i.IndustryCode, v)}
                      placeholder="RC26ERP_TEMPLATE_…"
                      availableDbs={availableDbs}
                      testId={`template-db-default-${i.IndustryCode}`}
                    />
                  </td>
                  {tiers.map(t => {
                    const k = `${i.IndustryCode}|${t}`;
                    const val = overrideMap[k] || '';
                    return (
                      <td key={t} style={td(150)}>
                        <div style={{ display: 'flex', gap: 4 }}>
                          <DBPicker
                            value={val}
                            onChange={v => setOverride(i.IndustryCode, t, v)}
                            placeholder={defaults[i.IndustryCode] || 'inherit ↑'}
                            availableDbs={availableDbs}
                            testId={`template-db-override-${i.IndustryCode}-${t}`}
                          />
                          {val && (
                            <button
                              onClick={() => deleteOverride(i.IndustryCode, t)}
                              className="erp-btn"
                              title="Remove override"
                              data-testid={`template-db-override-clear-${i.IndustryCode}-${t}`}
                              style={{ padding: '2px 6px' }}
                            >
                              <Trash2 size={10} />
                            </button>
                          )}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}
        <div style={{ marginTop: 8, padding: '6px 10px', background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 6, fontSize: 10, color: '#92400e', display: 'flex', alignItems: 'center', gap: 6 }}>
          <AlertTriangle size={11} />
          <span>Override blanks inherit the industry default. Industry default blanks inherit <code>RC26ERP_DEV</code>.</span>
        </div>
      </div>
      )}
    </div>
  );
}

function th(w) { return { width: w, padding: '6px 10px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: '#475569', borderBottom: '2px solid #cbd5e1', borderRight: '1px solid #e2e8f0', whiteSpace: 'nowrap' }; }
function td(w) { return { width: w, padding: '6px 8px', borderBottom: '1px solid #f1f5f9', borderRight: '1px solid #f1f5f9', verticalAlign: 'top' }; }
